import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Wallet, Copy, Check, ExternalLink, Sparkles, Zap, Shield, Award } from 'lucide-react';
import { SiX, SiDiscord, SiGithub } from '@icons-pack/react-simple-icons';
import LiquidGlassPassport from '../features/LiquidGlassPassport';
import { AMPOINTS_THRESHOLD, SPRING_CONFIG } from '../../utils/constants';
import { formatNumber } from '../../utils/formatters';

// Enhanced Progress Arc Component
const PremiumProgressArc = ({ progress, size = 200, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI * 1.5;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div 
      className="relative cursor-pointer" 
      style={{ width: size, height: size }}
      onMouseEnter={() => setHovered(true)} 
      onMouseLeave={() => setHovered(false)}
      whileHover={{ scale: 1.05 }}
      transition={SPRING_CONFIG}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-45"
        style={{ filter: 'drop-shadow(0 0 24px rgba(21,255,117,0.4))' }}
      >
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#15FF75" />
            <stop offset="50%" stopColor="#2CF98E" />
            <stop offset="100%" stopColor="#15FF75" />
          </linearGradient>
        </defs>
        
        <circle
          cx={size/2}
          cy={size/2}
          r={radius}
          fill="none"
          stroke="rgba(21, 255, 117, 0.1)"
          strokeWidth={strokeWidth}
        />
        
        <path
          d={`M ${size/2} ${strokeWidth/2}
              A ${radius} ${radius} 0 1 1 ${strokeWidth/2} ${size/2}`}
          fill="none"
          stroke="rgba(21, 255, 117, 0.15)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        <motion.path
          d={`M ${size/2} ${strokeWidth/2}
              A ${radius} ${radius} 0 1 1 ${strokeWidth/2} ${size/2}`}
          fill="none"
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 2.5, ease: "easeOut" }}
        />
        
        <motion.circle
          cx={size/2}
          cy={strokeWidth/2}
          r="4"
          fill="#15FF75"
          animate={{ 
            cx: size/2 + radius * Math.cos((progress / 100) * Math.PI * 1.5 - Math.PI/2),
            cy: size/2 + radius * Math.sin((progress / 100) * Math.PI * 1.5 - Math.PI/2)
          }}
          transition={{ duration: 2.5, ease: "easeOut" }}
        />
      </svg>
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="text-4xl font-bold bg-gradient-to-br from-[#E8FDF2] to-[#15FF75] bg-clip-text text-transparent"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, ...SPRING_CONFIG }}
          >
            {Math.round(progress)}%
          </motion.div>
          <div className="text-sm text-[#8AA5A2] mt-1 font-medium">Progress</div>
        </div>
      </div>
      
      {hovered && (
        <motion.div
          className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 bg-[#0D1220]/95 backdrop-blur-xl border border-[#15FF75]/30 rounded-2xl px-4 py-3 text-sm text-[#E8FDF2] whitespace-nowrap z-50"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="font-medium">
            {progress < 100 ? 
              `${formatNumber(AMPOINTS_THRESHOLD - (progress / 100 * AMPOINTS_THRESHOLD))} points to next level` : 
              'Maximum level achieved! 🚀'
            }
          </div>
          <div className="text-xs text-[#8AA5A2] mt-1">Keep earning to unlock rewards</div>
        </motion.div>
      )}
    </motion.div>
  );
};

const ProfileSidebar = ({ 
  userData, 
  currentAvatar, 
  level, 
  levelTitle,
  currentPrimaryName,
  address,
  copiedField,
  setCopiedField
}) => {
  
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const progressToThreshold = Math.min((userData.points / AMPOINTS_THRESHOLD) * 100, 100);
  const pointsNeeded = Math.max(0, AMPOINTS_THRESHOLD - userData.points);

  return (
    <motion.div 
      className="w-full flex-shrink-0"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="h-full flex flex-col gap-6">
        
        {/* Liquid Glass amPassport Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <LiquidGlassPassport 
            userData={userData}
            currentAvatar={currentAvatar}
            level={level}
            levelTitle={levelTitle}
          />
        </motion.div>

        {/* Progress to Passport Section - Clean Design with Arc */}
        <motion.div
          className="relative bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ filter: 'drop-shadow(0 0 20px rgba(0,255,157,0.1))' }}
        >
          {/* Transparent glow behind card */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#FFD964]/40 rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#00FF9D]/40 rounded-full blur-2xl" />
          </div>

          <div className="relative flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-[#FFD964]" />
              Passport Progress
            </h3>
            {progressToThreshold >= 100 && (
              <motion.div
                className="px-3 py-1 bg-[#00FF9D]/10 rounded-full border border-[#00FF9D]/20"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400 }}
                style={{ filter: 'drop-shadow(0 0 8px rgba(0,255,157,0.3))' }}
              >
                <span className="text-xs font-medium text-[#00FF9D]">ELIGIBLE</span>
              </motion.div>
            )}
          </div>

          {/* Progress Arc instead of bar */}
          <div className="flex justify-center mb-6">
            <PremiumProgressArc progress={progressToThreshold} size={160} strokeWidth={6} />
          </div>

          {/* Stats Row with translucent styling */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0A0E1B]/50 backdrop-blur-sm rounded-xl p-3">
              <div className="text-xs text-[#7C8694] mb-1">Current Points</div>
              <div className="text-lg font-bold text-[#00FF9D]">{userData.points.toLocaleString()}</div>
            </div>
            <div className="bg-[#0A0E1B]/50 backdrop-blur-sm rounded-xl p-3">
              <div className="text-xs text-[#7C8694] mb-1">Points Needed</div>
              <div className="text-lg font-bold text-[#FFD964]">{pointsNeeded.toLocaleString()}</div>
            </div>
          </div>
        </motion.div>

        {/* Connected Platforms - Clean Grid with Glows */}
        <motion.div
          className="relative bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ filter: 'drop-shadow(0 0 15px rgba(62,190,255,0.1))' }}
        >
          {/* Transparent glow behind card */}
          <div className="absolute inset-0 opacity-15 pointer-events-none">
            <div className="absolute top-1/2 right-0 w-16 h-16 bg-[#3EBEFF]/50 rounded-full blur-xl" />
            <div className="absolute bottom-0 left-1/2 w-20 h-20 bg-[#00FF9D]/30 rounded-full blur-xl" />
          </div>

          <div className="relative">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#3EBEFF]" />
              Connections
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { 
                  icon: SiX, 
                  active: userData.xId !== "Not linked", 
                  link: userData.xId !== "Not linked" ? `https://x.com/${userData.xId}` : null,
                  color: "#FFFFFF",
                  label: "X",
                  username: userData.xId !== "Not linked" ? `@${userData.xId}` : "Connect"
                },
                { 
                  icon: Globe, 
                  active: !!currentPrimaryName, 
                  link: currentPrimaryName ? `https://${currentPrimaryName}.ar-io.dev` : null,
                  color: "#00FF9D",
                  label: "ArNS",
                  username: currentPrimaryName || "Set Primary"
                },
                { 
                  icon: SiDiscord, 
                  active: true, 
                  link: null,
                  color: "#5865F2",
                  label: "Discord",
                  username: userData.userId?.substring(0, 8) || "Connected"
                },
                { 
                  icon: SiGithub, 
                  active: userData.githubId !== "Not linked", 
                  link: userData.githubId !== "Not linked" ? `https://github.com/${userData.githubId}` : null,
                  color: "#9FA6B2",
                  label: "GitHub",
                  username: userData.githubId !== "Not linked" ? userData.githubId : "Connect"
                }
              ].map((platform, i) => {
                const Icon = platform.icon;
                return (
                  <motion.button
                    key={i}
                    className={`group relative rounded-xl p-4 transition-all duration-300 backdrop-blur-sm ${
                      platform.active 
                        ? 'bg-[#0A0E1B]/50 hover:bg-[#0A0E1B]/70' 
                        : 'bg-[#0A0E1B]/30 opacity-60 hover:opacity-80'
                    }`}
                    onClick={() => platform.link && window.open(platform.link, '_blank')}
                    whileHover={{ y: -1, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + i * 0.05 }}
                    style={{ filter: platform.active ? `drop-shadow(0 0 8px ${platform.color}20)` : 'none' }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center backdrop-blur-sm"
                        style={{ backgroundColor: platform.active ? `${platform.color}15` : '#0A0E1B60' }}
                      >
                        <Icon 
                          className="w-5 h-5 transition-colors duration-300"
                          style={{ color: platform.active ? platform.color : '#5C6370' }}
                        />
                      </div>
                      <div className="w-full text-center">
                        <div className="text-xs font-medium text-[#7C8694]">{platform.label}</div>
                        <div className={`text-xs font-medium truncate ${
                          platform.active ? 'text-white' : 'text-[#5C6370]'
                        }`}>
                          {platform.username}
                        </div>
                      </div>
                    </div>

                    {platform.active && platform.link && (
                      <ExternalLink className="absolute top-2 right-2 w-3 h-3 text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Wallet Section - Clean Design with Glow */}
        <motion.div
          className="relative bg-[#10131F] rounded-2xl p-6 border border-white/5 shadow-lg shadow-black/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{ filter: 'drop-shadow(0 0 15px rgba(62,190,255,0.1))' }}
        >
          {/* Transparent glow behind card */}
          <div className="absolute inset-0 opacity-15 pointer-events-none">
            <div className="absolute top-0 left-1/3 w-16 h-16 bg-[#3EBEFF]/40 rounded-full blur-xl" />
            <div className="absolute bottom-0 right-1/4 w-18 h-18 bg-[#00FF9D]/30 rounded-full blur-xl" />
          </div>

          <div className="relative">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#3EBEFF]" />
              Active Wallet
            </h3>

            <div className="bg-[#0A0E1B]/50 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#00FF9D]/15 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-[#00FF9D]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#7C8694] mb-1">Wallet Address</div>
                    <div className="font-mono text-sm text-white">
                      {address.substring(0, 6)}...{address.substring(address.length - 6)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(address, 'active-wallet')}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <AnimatePresence mode="wait">
                    {copiedField === 'active-wallet' ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Check className="w-4 h-4 text-[#00FF9D]" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Copy className="w-4 h-4 text-[#7C8694] hover:text-white" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* View Onchain CTA - Enhanced with Glow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <motion.button
            className="relative w-full group overflow-hidden rounded-2xl bg-[#10131F] border border-white/5 shadow-lg shadow-black/20"
            onClick={() => window.open(`https://ao.link/entity/${userData.processId}`, '_blank')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ filter: 'drop-shadow(0 0 20px rgba(0,255,157,0.15))' }}
          >
            {/* Subtle animated background with glow */}
            <motion.div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 157, 0.1) 0%, rgba(62, 190, 255, 0.1) 50%, rgba(255, 217, 100, 0.1) 100%)'
              }}
            />
            
            {/* Content */}
            <div className="relative flex items-center justify-center gap-3 px-6 py-4 backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-[#00FF9D]" />
              <span className="font-semibold text-white">View Onchain Profile</span>
              <ExternalLink className="w-4 h-4 text-[#7C8694] group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
            </div>
          </motion.button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProfileSidebar;